import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  IElementType,
  isIElementMarkup,
  LabelType,
  MarkupIElement,
} from "@faro-lotv/ielement-types";
import { newestToOldest } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

/**
 * @param markup The markup element to check
 * @returns true if the markup has AI-detected labels
 */
export function hasAiDetectedLabels(markup: MarkupIElement): boolean {
  return getAiDetectedTag(markup) !== undefined;
}

/**
 * @param markup The markup element to get the AI-detected tag for
 * @returns the AI-detected tag or undefined if not found
 */
export function getAiDetectedTag(markup: MarkupIElement): string | undefined {
  return markup.labels?.find(
    (label) => label.labelType === LabelType.AiDetectedAsset,
  )?.name;
}

/**
 * @returns All AI-detected markup elements in the project
 */
export const selectAiDetectedMarkups = createSelector(
  [selectIElementsSlice],
  (iElementsSlice) => {
    const allElements = Object.values(iElementsSlice.iElements);
    return allElements
      .filter(
        (element): element is MarkupIElement =>
          element !== undefined && isIElementMarkup(element),
      )
      .filter((markup) => markup.type === IElementType.markup)
      .filter(hasAiDetectedLabels)
      .sort((a, b) => {
        // Primary sort: newest to oldest
        const dateSort = newestToOldest(a, b);
        if (dateSort !== 0) return dateSort;

        // Secondary sort: alphabetically because the 'createAt' field is the same from one batch processing
        const tagA = getAiDetectedTag(a)?.toLowerCase() ?? "";
        const tagB = getAiDetectedTag(b)?.toLowerCase() ?? "";
        return tagA.localeCompare(tagB);
      });
  },
);

/**
 * @returns The IDs of AI-detected markups
 */
export const selectAiDetectedMarkupIds = createSelector(
  [selectAiDetectedMarkups],
  (markups) => markups.map((markup) => markup.id),
);

/**
 * @param markup The markup to check
 * @returns True if the markup has been reviewed
 */
export function hasReviewed(markup: MarkupIElement): boolean {
  return !!markup.labels?.some(
    (label) => label.labelType === LabelType.AiReviewStatus,
  );
}

/**
 * @returns The IDs of AI-detected markups that are not already reviewed
 */
export const selectAiDetectedMarkupsNotReviewed = createSelector(
  [selectAiDetectedMarkups],
  (markups) => markups.filter((markup) => !hasReviewed(markup)),
);

/**
 * @returns The count of AI-detected markups that are not already reviewed
 */
export const selectAiDetectedMarkupsNotReviewedCount = createSelector(
  [selectAiDetectedMarkupsNotReviewed],
  (markups) => markups.length,
);

/**
 * @returns The count of all AI-detected markups
 */
export const selectAiDetectedMarkupsCount = createSelector(
  [selectAiDetectedMarkups],
  (markups) => markups.length,
);

/**
 * @returns The first AI-detected markup ID that is not reviewed, or undefined if none
 */
export const selectFirstAiDetectedMarkupNotReviewedId = createSelector(
  [selectAiDetectedMarkupsNotReviewed],
  (markups) => markups[0]?.id,
);
